import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { parseLocaleXml } from './parseLocaleXml';

const STORAGE_KEY = 'solvetax_locale';

function readStoredLocale() {
  if (typeof window === 'undefined') return null;
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'en' || v === 'te' || v === 'hi' ? v : null;
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const stored = typeof window !== 'undefined' ? readStoredLocale() : null;
  const [locale, setLocale] = useState(() => stored || 'en');
  const [strings, setStrings] = useState({});
  const [loadingStrings, setLoadingStrings] = useState(true);
  const hasLoadedOnceRef = useRef(false);

  const loadBundle = useCallback(async (loc) => {
    // Only show the full-page loading state for the very first bundle load.
    // On a later locale switch, keep the current page mounted (and the user's
    // scroll position intact) while the new strings load in the background.
    if (!hasLoadedOnceRef.current) setLoadingStrings(true);
    try {
      const base = (typeof process !== 'undefined' && process.env.PUBLIC_URL) || '';
      const res = await fetch(`${base}/locales/${loc}.xml`);
      if (!res.ok) throw new Error(String(res.status));
      const text = await res.text();
      setStrings(parseLocaleXml(text));
    } catch {
      setStrings({});
    } finally {
      hasLoadedOnceRef.current = true;
      setLoadingStrings(false);
    }
  }, []);

  useEffect(() => {
    loadBundle(locale);
  }, [locale, loadBundle]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = locale === 'te' || locale === 'hi' ? locale : 'en';
  }, [locale]);

  const setLocalePreference = useCallback((loc) => {
    if (loc !== 'en' && loc !== 'te' && loc !== 'hi') return;
    localStorage.setItem(STORAGE_KEY, loc);
    setLocale(loc);
  }, []);

  const t = useCallback((key, fallback = '', vars) => {
    let s = strings[key];
    if (s == null) {
      s = fallback !== undefined && fallback !== '' ? fallback : key;
    } else if (s === '') {
      s = fallback !== undefined ? fallback : key;
    }
    if (vars && typeof vars === 'object') {
      Object.entries(vars).forEach(([k, v]) => {
        s = String(s).split(`{${k}}`).join(String(v));
      });
    }
    return s;
  }, [strings]);

  const value = useMemo(() => ({
    locale,
    loadingStrings,
    stringsReady: !loadingStrings,
    setLocalePreference,
    t,
  }), [locale, loadingStrings, setLocalePreference, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
